import { APP } from '@advanza/advanza_generic'
import { Col, Row } from '@advanza/grid'
import { Divider, Icon } from '@advanza/ui'
import clsx from 'clsx'
import TranslateButton from 'components/TranslateButton'
import React from 'react'

interface TimeLineFile {
    file_id: string | number
    bucket: string
    file_key: string
    filename: string
}

interface TimeLineItem {
    message_id?: string | number
    log_id?: string | number
    log_type?: string
    type?: string
    files?: TimeLineFile[]
    body?: string
    is_pro?: boolean
    is_bot?: boolean
    deltaReadable?: string
    label?: React.ReactNode
    msg?: React.ReactNode
}

interface MatchTimeLineProps {
    timeLine: TimeLineItem[]
}

// Helper function to format body text with line breaks
const formatBodyText = (body: string | undefined): React.ReactNode[] | null => {
    if (!body) return null
    return body.split('\n').map((line, key) => {
        if (line.length === 0) {
            return <br key={key} />
        }
        return (
            <span key={key}>
                {line}
                <br />
            </span>
        )
    })
}

// Helper function to get unique key for timeline item
const getItemKey = (item: TimeLineItem, index: number): string => {
    if (item.message_id) return `message-${item.message_id}`
    if (item.log_id) return `log-${item.log_id}`
    return `item-${index}`
}

const MatchTimeLine = ({ timeLine }: MatchTimeLineProps) => {
    // Empty state handling
    if (!timeLine || timeLine.length === 0) {
        return <div className="text-text-tertiary-500 text-md px-4">No timeline items</div>
    }

    const renderLogMessage = (item: TimeLineItem) => {
        return (
            <div className="bg-white rounded-lg py-[9px] px-4 border border-grey-500 leading-6 min-w-[100px] relative">
                {item.log_id ? item.log_type : `${item.type} 'Message'`}
            </div>
        )
    }

    const renderFileMessage = (item: TimeLineItem) => {
        if (!item.files || item.files.length === 0) return null

        return item.files.map((file) => {
            const urlBase =
                file.bucket.indexOf('private') === -1 ? APP.S3_PUBLIC() : APP.S3_PRIVATE()
            return (
                <div
                    key={file.file_id}
                    className="bg-white rounded-lg py-[9px] px-4 border border-grey-500 leading-6 min-w-[100px] relative">
                    {item.body && (
                        <>
                            <p>{formatBodyText(item.body)}</p>
                            <TranslateButton text={item.body} targetLanguage="en" />
                        </>
                    )}
                    <small>Attachment</small>
                    <br />
                    <a
                        target="_blank"
                        rel="noopener noreferrer"
                        href={`${urlBase}${file.file_key}`}>
                        <Icon name="cloud_download" /> {file.filename}
                    </a>
                </div>
            )
        })
    }

    const renderTextMessage = (item: TimeLineItem) => {
        return (
            <div
                className={clsx(
                    'rounded-lg py-[9px] px-4 leading-6 min-w-[100px] relative',
                    item.is_pro
                        ? 'bg-primary-100 border-0 rounded-tr-[2px] after:content-[""] after:block after:w-[10px] after:h-[10px] after:border-t-[8px] after:border-t-primary-100 after:border-b-0 after:border-r-[5px] after:border-r-transparent after:border-l-0 after:absolute after:right-[-5px] after:top-0'
                        : 'bg-white border border-grey-500',
                )}>
                {formatBodyText(item.body)}
                <TranslateButton text={item.body || ''} targetLanguage="en" />
            </div>
        )
    }

    const renderItem = (item: TimeLineItem) => {
        // Checkpoint/event items (no message_id or log_id)
        if (!item.message_id && !item.log_id) {
            return <div>{item.label || item.msg}</div>
        }

        // Log message type
        if (item.type || item.log_id) {
            return renderLogMessage(item)
        }

        // File attachment message
        if (item.files && item.files.length > 0) {
            return renderFileMessage(item)
        }

        // Regular text message
        return renderTextMessage(item)
    }

    return (
        <div>
            {timeLine.map((item, index) => {
                const uniqueKey = getItemKey(item, index)
                return (
                    <Row
                        key={uniqueKey}
                        className={
                            item.is_bot || item.log_id
                                ? 'j-center center'
                                : item.is_pro
                                  ? 'tright'
                                  : ''
                        }>
                        <Col xs={6}>
                            <div>
                                <small
                                    className={clsx(
                                        'text-text-tertiary-500 text-sm px-4',
                                        item.is_pro && 'text-right',
                                    )}>
                                    {item.deltaReadable} later
                                </small>
                            </div>
                            {renderItem(item)}
                            <Divider m />
                        </Col>
                    </Row>
                )
            })}
        </div>
    )
}

export default MatchTimeLine
